import { useState } from 'react'
import { Search, UserPlus } from 'lucide-react'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import { useContactsPage } from '@/features/contacts/useContacts'
import { useAddToList } from '@/features/lists/useLists'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import type { ContactList } from '@/api/lists'

/** Pick contacts to add to a static list: search, tick several, add them in one go. */
export default function AddContactsModal({ list, onClose }: { list: ContactList; onClose: () => void }) {
  const add = useAddToList()
  const [query, setQuery] = useState('')
  const q = useDebouncedValue(query.trim(), 300)
  const { data, isFetching } = useContactsPage({ q: q || undefined, page: 0, size: 50, sort: 'name', dir: 'asc' })
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [added, setAdded] = useState<number | null>(null)
  const toggle = (id: string) =>
    setPicked((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const contacts = data?.items ?? []

  return (
    <Modal
      title={`Add contacts to ${list.name}`}
      icon={<UserPlus size={17} className="text-brand-primary" />}
      onClose={onClose}
      size="lg"
      footer={
        added != null ? (
          <button type="button" onClick={onClose} className={modalButton.primary}>Done</button>
        ) : (
          <>
            {add.isError && <p className="text-danger text-xs mr-auto">{apiErrorMessage(add.error)}</p>}
            <span className="text-sm text-gray-500 mr-auto">{picked.size} selected</span>
            <button type="button" onClick={onClose} className={modalButton.ghost}>Cancel</button>
            <button
              type="button"
              disabled={picked.size === 0 || add.isPending}
              onClick={() => add.mutate({ listId: list.id, contactIds: [...picked] }, { onSuccess: (r) => setAdded(r.added) })}
              className={modalButton.primary}
            >
              {add.isPending ? 'Adding…' : `Add ${picked.size || ''} contact${picked.size === 1 ? '' : 's'}`}
            </button>
          </>
        )
      }
    >
      {added != null ? (
        <p className="text-sm text-gray-700">
          {added === 0 ? 'They were all on the list already.' : `${added} contact${added === 1 ? '' : 's'} added.`}
        </p>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email or phone…"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
            />
          </div>
          <ul className="max-h-96 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100" aria-busy={isFetching}>
            {contacts.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-gray-400">{isFetching ? 'Searching…' : 'No contacts match.'}</li>
            )}
            {contacts.map((c) => {
              const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Unnamed contact'
              return (
                <li key={c.id}>
                  <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={picked.has(c.id)} onChange={() => toggle(c.id)} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-brand-darkest truncate">{name}</span>
                      <span className="block text-xs text-gray-500 truncate">
                        {[c.email, c.mobile || c.phone].filter(Boolean).join(' · ') || 'No email or phone'}
                      </span>
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>
          {(data?.total ?? 0) > contacts.length && (
            <p className="text-xs text-gray-400">Showing the first {contacts.length} of {data?.total} — search to narrow down.</p>
          )}
        </div>
      )}
    </Modal>
  )
}
